import { formatSource } from "@validator/schema";

import { Alert } from "@shared/components/Alert";

import { T } from "@/components/i18n";
import { ProgressBar } from "@/components/submission/ProgressBar";
import { ValidateArtifact } from "@/types/submission";

import { CheckCircle, CloudAlert, LoaderCircle, ScanText } from "lucide-react";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

// Uploading state

export function UploadProgressAlert({ progress }: { progress: number }) {
  return (
    <div>
      <div className="text-gray-600 dark:text-neutral-400 flex gap-x-1.5 items-center mb-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 0.6, repeat: Infinity, ease: "linear" }}
        >
          <LoaderCircle className="stroke-blue-500" size={20} strokeWidth={3} />
        </motion.div>
        <span className="font-medium text-lg">
          <T i18nKey="components.alerts.uploadProgress.status" />
        </span>
      </div>
      <ProgressBar progress={progress} />
    </div>
  );
}

// Validated state

interface ValidDataAlertProps {
  result: ValidateArtifact | null;
  text: React.ReactNode;
  showSource?: boolean;
  children?: React.ReactNode;
}

export function ValidDataAlert({
  result,
  text,
  showSource = false,
  children,
}: ValidDataAlertProps) {
  if (!result) return null;

  const source =
    result.type === "success" ? (
      <span>
        Identified the source as{" "}
        <span className="font-bold">{formatSource(result.data.source)}</span>.{" "}
      </span>
    ) : null;

  return (
    <Alert
      variant="info"
      status="Validation successful"
      text={
        <>
          {showSource && source}
          {text}
        </>
      }
      Icon={ScanText}
    >
      {children}
    </Alert>
  );
}

// Upload success state

interface UploadSuccessAlertProps {
  children?: React.ReactNode;
  showReport: boolean;
}

export function UploadSuccessAlert({ children, showReport }: UploadSuccessAlertProps) {
  const { t } = useTranslation();
  return (
    <Alert
      variant="success"
      status="Upload successful"
      text={
        showReport
          ? t("components.alerts.uploadSuccess.reportAlert")
          : t("components.alerts.uploadSuccess.noReportAlert")
      }
      Icon={CheckCircle}
    >
      {children}
    </Alert>
  );
}

// Upload error state

export function UploadErrorAlert({
  message,
  children,
}: {
  message: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Alert
      variant="error"
      status={t("components.alerts.uploadError.status")}
      text={t("components.alerts.uploadError.alert")}
      Icon={CloudAlert}
      message={message}
    >
      {children}
    </Alert>
  );
}
